import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Bell, Mail } from 'lucide-react';
import type {
  NotificationSubscriptionDomainNode,
  NotificationSubscriptionEventNode,
} from '../../lib/api';
import type { SubscriptionChannel } from './useSubscriptionTree';

interface SubscriptionDomainRowProps {
  domain: NotificationSubscriptionDomainNode;
  /** False when the tenant's master email switch is off. */
  emailsEnabled: boolean;
  saving: boolean;
  onMute: (
    scopeType: 'domain',
    scopeKey: string,
    channel: SubscriptionChannel,
    muted: boolean,
  ) => void;
}

/** The effective state of one channel on an event, with a safe default. */
function channelState(
  event: NotificationSubscriptionEventNode,
  channel: SubscriptionChannel,
) {
  return event.effective?.[channel] ?? { muted: false };
}

/**
 * Is a whole domain muted on this channel?
 *
 * Domain nodes carry only the user's own row, not a resolved state, so the
 * answer is read from the events beneath them: those *are* resolved, and a
 * domain-level mute is exactly what makes every one of them muted. Reading the
 * row alone would show nothing for a mute inherited from the global scope.
 */
function domainMuted(
  events: NotificationSubscriptionEventNode[],
  channel: SubscriptionChannel,
) {
  const live = events.filter(event => event.tenant_enabled);
  if (live.length === 0) return false;
  return live.every(event => channelState(event, channel).muted);
}

/**
 * One section of a person's notification settings.
 *
 * On or off, per section, and nothing else. Which events exist, on which
 * channels and how often they send is the site's decision in Admin Setup —
 * putting any of that in front of every user is asking them to re-make an
 * administrator's call. The only question left for a person is whether they
 * want a section at all.
 */
export function SubscriptionDomainRow({
  domain, emailsEnabled, saving, onMute,
}: SubscriptionDomainRowProps) {
  const events: NotificationSubscriptionEventNode[] =
    domain.subdomains.flatMap(sub => sub.events);
  const bellMuted = domainMuted(events, 'bell');
  const emailMuted = domainMuted(events, 'email');

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border p-4">
      <div className="flex flex-1 items-center gap-2">
        <span className="font-medium">{domain.label}</span>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">In-app</Label>
          <Switch
            checked={!bellMuted}
            disabled={saving}
            onCheckedChange={next =>
              onMute('domain', domain.scope_key, 'bell', !next)}
            aria-label={`In-app notifications for ${domain.label}`}
          />
        </div>

        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">Email</Label>
          <Switch
            checked={emailsEnabled && !emailMuted}
            // A tenant that has turned email off entirely is not something this
            // switch can override, so it reads as off and says why rather than
            // pretending to work.
            disabled={saving || !emailsEnabled}
            onCheckedChange={next =>
              onMute('domain', domain.scope_key, 'email', !next)}
            aria-label={`Email notifications for ${domain.label}`}
          />
        </div>
      </div>
    </div>
  );
}
